import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Card } from '../../_shared/models/card-models/card';
import { CardItem } from '../../_shared/models/card-models/card-item';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { CardType } from '../../_shared/models/card-models/CardType';
import { EarningsResponse } from '../../_shared/models/earning-models/earnings-response';
import { ApiService } from '../../_shared/services/api.service';
import { CardLookupRequest, CardLookupResponse } from '../../_shared/models/card-models/card-lookup-model';

@Injectable({
  providedIn: 'root'
})
export class CardService implements OnDestroy {
  rewardCards = new BehaviorSubject<Card[]>([]);
  cardTypeMap = new Map<CardType, Card>([
    //TODO: Verify if "path" s are correct.
    [
      CardType.NGMC,
      {
        name: 'My GM Rewards Card',
        path: 'ngmc',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'core',
        analyticsTypeOwned: 'core',
        analyticsLoginType: 'core'
      }
    ],
    [
      CardType.FLEX,
      {
        name: 'My GM Rewards Card',
        path: 'flex',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'flex',
        analyticsTypeOwned: 'flex',
        analyticsLoginType: 'flex'
      }
    ],

    [
      CardType.BLUE,
      {
        name: 'GM&trade; Mastercard&reg;',
        path: 'bluegold',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'bluegold',
        analyticsTypeOwned: 'core',
        analyticsLoginType: 'bluegold'
      }
    ],
    [
      CardType.GOLD,
      {
        name: 'GM Card',
        path: 'bluegold',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'bluegold',
        analyticsTypeOwned: 'core',
        analyticsLoginType: 'bluegold'
      }
    ],
    [
      CardType.COPPER,
      {
        name: 'GM&trade; Mastercard&reg;',
        path: 'copperplatinum',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'platinum',
        analyticsTypeOwned: '',
        analyticsLoginType: 'platinum'
      }
    ],
    [
      CardType.PLATINUM,
      {
        name: 'GM Card',
        path: 'copperplatinum',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'platinum',
        analyticsTypeOwned: '',
        analyticsLoginType: 'platinum'
      }
    ],
    [
      CardType.CHROME,
      {
        name: 'GM Card',
        path: 'chrome',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'platinum',
        analyticsTypeOwned: '',
        analyticsLoginType: 'platinum'
      }
    ],
    [
      CardType.WORLDPLAT,
      {
        name: 'GM Card',
        path: 'worldplat',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'platinum',
        analyticsTypeOwned: '',
        analyticsLoginType: 'platinum'
      }
    ],
    [
      CardType.WORLDREG,
      {
        name: 'GM Card',
        path: 'worldpreg',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'platinum',
        analyticsTypeOwned: '',
        analyticsLoginType: 'platinum'
      }
    ],
    [
      CardType.WORLDFLEX,
      {
        name: 'GM Card',
        path: 'worldflex',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'flex',
        analyticsTypeOwned: 'flex',
        analyticsLoginType: 'flex'
      }
    ],

    [
      CardType.EFC,
      {
        name: 'GM Extended&trade; Family Mastercard&reg;',
        path: 'efc',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'efc',
        analyticsTypeOwned: 'efc',
        analyticsLoginType: 'efc'
      }
    ],
    [
      CardType.BPBC,
      {
        name: 'GM Business&trade; Mastercard&reg;',
        path: 'bpbc',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'bpbc',
        analyticsTypeOwned: 'business',
        analyticsLoginType: 'bpc business'
      }
    ],
    [
      CardType.GMB,
      {
        name: 'GM Mastercard Business Card',
        path: 'gmb',
        trackId: '',
        last4CardNumber: '',
        analyticsType: 'gmb',
        analyticsTypeOwned: 'gmb',
        analyticsLoginType: 'gmb business'
      }
    ],
    [
      CardType.NoCardFound,
      {
        name: 'No CARD FOUND',
        path: '',
        trackId: '',
        last4CardNumber: '',
        analyticsType: '',
        analyticsTypeOwned: '',
        analyticsLoginType: ''
      }
    ]
  ]);
  private earningsSource = new BehaviorSubject<EarningsResponse>(null);
constructor(private apiService:ApiService) {}

  currentCardSource = new BehaviorSubject<Card>({
    name: '',
    path: '',
    trackId: '',
    last4CardNumber: '',
    analyticsType: '',
    analyticsTypeOwned: '',
    analyticsLoginType: ''
  });

  changeCurrentCard(i: number): void {
    this.currentCardSource.next(this.rewardCards.value[i]);
  }

  setCurrentCard(trackId: string): void {
    this.rewardCards
      .asObservable()
      .pipe(map(cards => cards.find(card => card.trackId === trackId)))
      .subscribe(card => {
        this.currentCardSource.next(card);
      });
  }
  setEarnings(earnings: EarningsResponse): void {
    this.earningsSource.next(earnings);
  }

  getEarnings(): Observable<EarningsResponse> {
    return this.earningsSource.asObservable();
  }

  getUsersRewardCards(): Observable<Card[]> {
    return this.rewardCards.asObservable();
  }

  getCurrentCard(): Observable<Card> {
    return this.currentCardSource.asObservable();
  }

  setUserCards(cards: CardItem[]): any {
    const allCards = [];
    cards.forEach(cardItem => {
      allCards.push({
        ...this.cardTypeMap.get(cardItem.cardType),
        trackId: cardItem.trackId,
        last4CardNumber: cardItem.last4CardNumber.toString().padStart(4, '0')
      });
    });
    this.rewardCards.next(allCards);
    this.currentCardSource.next(allCards[0]);
  }


  fetchAndSetEarnings(trackId: string): Observable<EarningsResponse> {
    return this.apiService.getEarningsStatement(trackId).pipe(
      tap(earnings => this.setEarnings(earnings))
    );
  }

  getEarningsWithFallback(trackId: string): Observable<EarningsResponse> {
    return this.getEarnings().pipe(
      switchMap(earnings => {
        if (earnings) {
          return of(earnings);
        } else {
          return this.fetchAndSetEarnings(trackId);
        }
      })
    );
  }

  clearCards() {
    this.currentCardSource.next(null);
    this.rewardCards.next(null);
  }

  ngOnDestroy(): void {
    this.currentCardSource.unsubscribe();
    this.rewardCards.unsubscribe();
    this.earningsSource.unsubscribe();
  }

  /**
   * Determines if notification banner should be shown based on card lookup response
   * @param cardLookupResponse The response from the card lookup API
   * @returns boolean indicating if notification should be shown
   */
  shouldShowNotificationBanner(cardLookupResponse: CardLookupResponse): boolean {
    // Only show notification for migrated customers (BPBC and GMB card types)
    if (cardLookupResponse.cardType !== 'BPBC' && cardLookupResponse.cardType !== 'GMB') {
      return false;
    }

    // For GMB card type, check enrollment date
    if (cardLookupResponse.cardType === 'GMB') {
      const enrollmentDate = new Date(cardLookupResponse.enrollmentDate);
      const cutoffDate = new Date('2025-08-07'); // 8/7 cutoff date

      // Show notification only if enrollment date was before 8/7
      return enrollmentDate < cutoffDate;
    }

    // For BPBC card type, show notification (migrated customers)
    return true;
  }


  /**
   * Performs card lookup and determines notification banner visibility
   * @param request Card lookup request
   * @returns Observable with notification banner visibility
   */
  checkNotificationBannerVisibility(request: CardLookupRequest): Observable<boolean> {
    return this.apiService.lookupCard(request).pipe(
      map(response => this.shouldShowNotificationBanner(response)),
      catchError(error => {
        return of(false);
      })
    );
  }

}
